/**
 * Aba Vendas assinadas (decisões 8 e 11 do Igor): as vendas que o comercial
 * registrou e o PCP ainda não ligou ao pedido da planilha. Elas já estão na
 * fila da fábrica (camada "venda assinada"); ligar tira da fila e passa a
 * valer a planilha.
 *
 * Todos com `:view` leem. Registrar: comercial (`:registrar_venda`). Ligar:
 * PCP (`:ligar_venda`). Cancelar: PCP ou quem registrou (o backend confere).
 * A sugestão "o pedido X parece ser esta venda" nunca liga sozinha.
 */
import React from "react";
import { FiPlus } from "react-icons/fi";

import { Aviso, Bloco, BotaoTexto, Ponto, Regua, Vazio } from "./styles";
import { BotaoAcao } from "./stylesSimulacao";
import { BotaoLinha, LinhaVenda, ListaVendas } from "./stylesVendas";
import {
  nomesDosAmbientes,
  situacaoDaVenda,
  textoDaEspera,
  textoDaSugestao,
  textoDasDatas,
  textoDosAmbientes,
} from "./apresentacaoVendas";

function LinhaDaVenda({ venda, sugeridos, podeLigar, podeCancelar, onLigar, onCancelar }) {
  const situacao = situacaoDaVenda(venda);
  const sugestao = textoDaSugestao(sugeridos);
  return (
    <LinhaVenda data-venda={venda.id}>
      <div className="texto">
        <span className="cliente">
          {situacao.tom ? <Ponto $tom={situacao.tom} aria-hidden="true" /> : null}
          <span title={venda.cliente}>{venda.cliente}</span>
        </span>
        <span className="meta">
          {textoDosAmbientes(venda.ambientes)} · {textoDasDatas(venda)}
        </span>
        <span className="sub">
          {nomesDosAmbientes(venda.ambientes)} · {textoDaEspera(venda)}
          {situacao.tom ? ` · ${situacao.texto}` : ""}
        </span>
        {venda.observacao ? <span className="sub">{venda.observacao}</span> : null}
        {sugestao ? (
          <span className="sugestao" role="note">
            {sugestao}
          </span>
        ) : null}
      </div>
      {podeLigar || podeCancelar ? (
        <div className="acoes">
          {podeLigar ? (
            <BotaoLinha type="button" onClick={() => onLigar(venda)}>
              {sugeridos?.length === 1 ? `Ligar ao pedido ${sugeridos[0].pedido}` : "Ligar ao pedido"}
            </BotaoLinha>
          ) : null}
          {podeCancelar ? (
            <BotaoLinha type="button" onClick={() => onCancelar(venda)}>
              Cancelar
            </BotaoLinha>
          ) : null}
        </div>
      ) : null}
    </LinhaVenda>
  );
}

/**
 * `vendas` = `useVendasAssinadas()`; `sugestoes` = `sugestoesDeLigacao(...)`;
 * `podeRegistrar`, `podeLigar`, `podeCancelarDe(venda)`; `onRegistrar()`,
 * `onLigar(venda)`, `onCancelar(venda)` abrem os painéis.
 */
export default function AbaVendasAssinadas({
  vendas,
  sugestoes,
  podeRegistrar,
  podeLigar,
  podeCancelarDe,
  onRegistrar,
  onLigar,
  onCancelar,
}) {
  const lista = vendas.vendas || [];
  return (
    <>
      <Regua>
        Venda com contrato assinado que o PCP ainda não digitou na planilha. Ela já ocupa lugar na fila da fábrica
        pela entrega desejada (selo "venda assinada, fora da planilha"). Quando o PCP digitar o pedido, ele liga a
        venda ao número: ela sai da fila e passam a valer as linhas da planilha. Depois de 30 dias sem ligar, vira
        aviso para o PCP.
      </Regua>

      {podeRegistrar ? (
        <BotaoAcao type="button" onClick={onRegistrar} style={{ alignSelf: "flex-start" }}>
          <FiPlus size={15} aria-hidden="true" />
          Registrar venda assinada
        </BotaoAcao>
      ) : null}

      <Bloco aria-label="Aguardando o PCP">
        <h2>
          Aguardando o PCP <span>{lista.length}</span>
        </h2>
        {!vendas.carregado ? <Vazio>Lendo as vendas assinadas…</Vazio> : null}
        {vendas.carregado && vendas.erro ? (
          <Aviso $tom="warning" role="note">
            <div>
              <strong>Não foi possível ler as vendas assinadas.</strong> {vendas.erro} A fila acima está sem elas.{" "}
              <BotaoTexto type="button" onClick={vendas.recarregar} style={{ minHeight: 0, padding: "0 4px" }}>
                Tentar de novo
              </BotaoTexto>
            </div>
          </Aviso>
        ) : null}
        {vendas.carregado && !vendas.erro && lista.length === 0 ? (
          <Vazio>Nenhuma venda assinada esperando o PCP.</Vazio>
        ) : null}
        {lista.length > 0 ? (
          <ListaVendas>
            {lista.map((venda) => (
              <LinhaDaVenda
                key={venda.id}
                venda={venda}
                sugeridos={sugestoes?.[venda.id]}
                podeLigar={podeLigar}
                podeCancelar={podeCancelarDe(venda)}
                onLigar={onLigar}
                onCancelar={onCancelar}
              />
            ))}
          </ListaVendas>
        ) : null}
      </Bloco>
    </>
  );
}
